import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
} from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import { randomId } from 'server/shared/helpers/random';
import { AssignmentTypeTabEnum } from 'server/shared/models/assignmentTypeTab.enum';
import { Certificate } from 'server/shared/models/certificate';
import { Customer } from 'server/shared/models/customer';
import { CustomerTariff, Tariff } from 'server/shared/models/customerTarifs';
import { RoleEnum } from 'server/shared/models/role.enum';
import { DispoUser, MappedCertificate } from 'server/shared/models/user';
import { CustomerService } from '../../services/customer.service';
import { ToastService } from '../../services/toast/toast.service';

interface CustomerTariffWithExcludedCertificates extends CustomerTariff {
  excludedCertificates: MappedCertificate[];
}
@Component({
  selector: 'admin-tariffs',
  templateUrl: 'tariffs.html',
  styleUrls: ['tariffs.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TariffsComponent implements OnDestroy {
  private _tab: string = '';
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);
  user$ = new BehaviorSubject<DispoUser | undefined>(undefined);
  initialJobCustomerTariffs: CustomerTariffWithExcludedCertificates[] = [];
  jobCustomerTariffs: CustomerTariffWithExcludedCertificates[] = [];
  initialEmploymentCustomerTariffs: CustomerTariffWithExcludedCertificates[] =
    [];
  employmentCustomerTariffs: CustomerTariffWithExcludedCertificates[] = [];

  resetForm$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  @ViewChild('tariffsForm', { static: false }) form?: NgForm;
  noEmptyCertificates$ = new BehaviorSubject<boolean>(false);

  tabs$ = new BehaviorSubject<
    { label: string; value: AssignmentTypeTabEnum }[]
  >([
    {
      label: 'jobsPool',
      value: AssignmentTypeTabEnum.JOBS,
    },
    {
      label: 'employmentsPool',
      value: AssignmentTypeTabEnum.EMPLOYMENTS,
    },
  ]);

  tabParam$: Observable<AssignmentTypeTabEnum> = this.route.queryParams.pipe(
    map((params) => params.tab ?? AssignmentTypeTabEnum.JOBS),
    // distinctUntilChanged is needed in case of other params changing
    distinctUntilChanged(),
  );

  customerTariffs$: Observable<CustomerTariffWithExcludedCertificates[]> =
    combineLatest([
      this.tabParam$,
      this.customerService.readyDispoCustomer$,
      this.customerService.readySharedPoolCustomers$,
      this.resetForm$,
    ]).pipe(
      map(([tab, customer, sharedPoolCustomers, replay]) => {
        this._tab = tab;
        this.initializeTariffs(customer, sharedPoolCustomers);
        if (
          this.jobCustomerTariffs.length !==
            this.initialJobCustomerTariffs?.length ||
          replay
        ) {
          this.jobCustomerTariffs = Array.from(this.initialJobCustomerTariffs);
        }
        if (
          this.employmentCustomerTariffs.length !==
            this.initialEmploymentCustomerTariffs?.length ||
          replay
        )
          this.employmentCustomerTariffs = [
            ...this.initialEmploymentCustomerTariffs,
          ];
        this.setExcludedCertificates();
        this.checkIfCertificatesAreEmpty();
        return tab === AssignmentTypeTabEnum.JOBS
          ? this.jobCustomerTariffs
          : this.employmentCustomerTariffs;
      }),
    );

  currentUserTracker?: Tracker.Computation;

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
  ) {}

  ngOnInit(): void {
    this.currentUserTracker = Tracker.autorun(() => {
      const user = Users.findOne({
        _id: Meteor.userId(),
        role: RoleEnum.DISPO,
      }) as DispoUser;

      let customer = Customers.findOne({ _id: user?.customerId });
      this.ngZone.run(() => {
        if (
          user &&
          !user.dispoPermissions?.dispoAccess?.featurePermissions
            ?.isCustomerAdmin
        ) {
          this.historyBack();
        }
        this.customer$.next(customer);
        this.user$.next(user);
      });
    });
  }

  ngOnDestroy() {
    this.currentUserTracker?.stop();
  }

  initializeTariffs(customer: Customer, sharedPoolCustomers?: Customer[]) {
    this.initialJobCustomerTariffs = [];
    this.initialEmploymentCustomerTariffs = [];
    // this is weird because cancel was wonky otherwise
    this.initialJobCustomerTariffs = JSON.parse(
      JSON.stringify(this.getJobCustomerTariffs(customer, sharedPoolCustomers)),
    );
    // this is weird because cancel was wonky otherwise
    this.initialEmploymentCustomerTariffs = JSON.parse(
      JSON.stringify(
        this.getEmploymentCustomerTariffs(customer, sharedPoolCustomers),
      ),
    );
  }

  getJobCustomerTariffs(
    customer: Customer,
    sharedPoolCustomers?: Customer[],
  ): CustomerTariffWithExcludedCertificates[] {
    const customerTransfers: { id: string; displayName: string }[] =
      customer.customerTransfers?.transfers?.map((transfer) => {
        return {
          id: transfer.targetCustomer.id,
          displayName: transfer.targetCustomer.displayName,
        };
      }) ?? [];
    const customerSharedPools: { id: string; displayName: string }[] =
      sharedPoolCustomers
        ?.filter((sharedPoolCustomer) => {
          return sharedPoolCustomer.enableJobs;
        })
        ?.map((sharedPoolCustomer) => {
          return {
            id: sharedPoolCustomer._id,
            displayName: sharedPoolCustomer.name,
          };
        }) ?? [];

    let customerTariffs: CustomerTariffWithExcludedCertificates[] =
      customer.customerTariffs?.jobTariffs?.map((tariff) => {
        return {
          ...tariff,
          excludedCertificates: [],
        };
      }) ?? [];

    return this.getCustomerTariffs(
      customerTariffs,
      customerTransfers,
      customerSharedPools,
    );
  }
  getEmploymentCustomerTariffs(
    customer: Customer,
    sharedPoolCustomers?: Customer[],
  ): CustomerTariffWithExcludedCertificates[] {
    const customerTransfers: { id: string; displayName: string }[] =
      customer.customerTransfers?.employmentTransfers?.map((transfer) => {
        return {
          id: transfer.targetCustomer.id,
          displayName: transfer.targetCustomer.displayName,
        };
      }) ?? [];
    const customerSharedPools: { id: string; displayName: string }[] =
      sharedPoolCustomers
        ?.filter((sharedPoolCustomer) => {
          return sharedPoolCustomer.enableEmployments;
        })
        ?.map((sharedPoolCustomer) => {
          return {
            id: sharedPoolCustomer._id,
            displayName: sharedPoolCustomer.name,
          };
        }) ?? [];

    let customerTariffs: CustomerTariffWithExcludedCertificates[] =
      customer.customerTariffs?.employmentTariffs?.map((tariff) => {
        return {
          ...tariff,
          excludedCertificates: [],
        };
      }) ?? [];

    return this.getCustomerTariffs(
      customerTariffs,
      customerTransfers,
      customerSharedPools,
    );
  }

  getCustomerTariffs(
    customerTariffs: CustomerTariffWithExcludedCertificates[],
    customerTransfers: { id: string; displayName: string }[],
    customerSharedPools: { id: string; displayName: string }[],
  ): CustomerTariffWithExcludedCertificates[] {
    const tariffCustomers: { id: string; displayName: string }[] =
      customerTransfers.concat(customerSharedPools);

    let duplicateTariffCustomerIds: string[] = [];
    tariffCustomers.forEach((tariffCustomer, index) => {
      if (duplicateTariffCustomerIds.includes(tariffCustomer.id)) {
        tariffCustomers.splice(index, 1);
      }
      duplicateTariffCustomerIds.push(tariffCustomer.id);
    });

    const currentTariffCustomerIds: string[] = customerTariffs.map(
      (currentTariff) => {
        return currentTariff.targetCustomer.id;
      },
    );

    if (
      currentTariffCustomerIds.length !== tariffCustomers.length ||
      !tariffCustomers.every((tariffCustomer) =>
        currentTariffCustomerIds.includes(tariffCustomer.id),
      )
    ) {
      tariffCustomers.forEach((tariffCustomer) => {
        if (!currentTariffCustomerIds.includes(tariffCustomer.id)) {
          customerTariffs.push({
            targetCustomer: tariffCustomer,
            tariffs: [],
            excludedCertificates: [],
          });
        }
      });
    }

    return customerTariffs?.filter((tariff) => {
      return tariffCustomers
        .map((customer) => customer.id)
        .includes(tariff.targetCustomer.id);
    });
  }

  switchTab(tab: string): void {
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tab },
        replaceUrl: true,
        queryParamsHandling: 'merge',
      })
      .then();
  }

  certificatesSelected(tariff: Tariff, certificates: Certificate[] = []) {
    if (certificates.length) {
      tariff.certificates = certificates.map((certificate) => {
        return {
          id: certificate._id,
          displayName: certificate.name,
        };
      });
    } else {
      tariff.certificates = [];
    }
    this.form?.control.markAsDirty();
    this.form?.control.markAsTouched();
    this.setExcludedCertificates();
    this.checkIfCertificatesAreEmpty();
  }

  setExcludedCertificates() {
    this.jobCustomerTariffs?.forEach((customerTariff) => {
      let excludedCertificates = customerTariff.excludedCertificates;
      customerTariff.tariffs.forEach((tariff) => {
        let certificates = tariff.certificates;
        if (certificates.length) {
          excludedCertificates = excludedCertificates.concat(certificates);
        }
      });
      customerTariff.excludedCertificates = excludedCertificates;
    });

    this.employmentCustomerTariffs?.forEach((customerTariff) => {
      let excludedCertificates = customerTariff.excludedCertificates;
      customerTariff.tariffs.forEach((tariff) => {
        let certificates = tariff.certificates;
        if (certificates.length) {
          excludedCertificates = excludedCertificates.concat(certificates);
        }
      });
      customerTariff.excludedCertificates = excludedCertificates;
    });
  }

  checkIfCertificatesAreEmpty() {
    if (this._tab === AssignmentTypeTabEnum.JOBS) {
      this.noEmptyCertificates$.next(
        this.jobCustomerTariffs.every((customerTariff) => {
          return customerTariff.tariffs.every((tariff) => {
            return tariff.certificates.length > 0;
          });
        }),
      );
    } else {
      this.noEmptyCertificates$.next(
        this.employmentCustomerTariffs.every((customerTariff) => {
          return customerTariff.tariffs.every((tariff) => {
            return tariff.certificates.length > 0;
          });
        }),
      );
    }
  }

  addNewTariff(customerTariff: CustomerTariff): void {
    customerTariff.tariffs.push({
      id: randomId(),
      certificates: [],
      hourlyWage: '',
    });
  }

  removeTariff(customerTariff: CustomerTariff, tariffId: string): void {
    customerTariff.tariffs = customerTariff.tariffs.filter(
      (t) => t.id !== tariffId,
    );
  }

  historyBack() {
    this.location.back();
  }
  cancelChanges() {
    this.resetForm$.next(true);
  }

  saveTariffs(): void {
    const customer = this.customer$.value;
    if (!customer) return;
    Meteor.call(
      'updateCustomerTariffs',
      customer._id,
      this.jobCustomerTariffs.map((tariff) => {
        return {
          targetCustomer: tariff.targetCustomer,
          tariffs: tariff.tariffs,
        };
      }),
      this.employmentCustomerTariffs.map((tariff) => {
        return {
          targetCustomer: tariff.targetCustomer,
          tariffs: tariff.tariffs,
        };
      }),
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }
}
